/**
 * Upward-normal canonicalisation for terrain tiles (issue #135).
 *
 * A terrain tile is a heightfield: its surface normals must point up. But the
 * pipeline's TIN export winds triangles inconsistently, and the LOD1 tiles
 * shipped today read as fully REVERSED — computeVertexNormals yields downward
 * normals for the whole mesh. The viewer tolerated that because the terrain
 * material is `side: DoubleSide`: viewed from above every triangle is a back
 * face, so three.js flips its normal up for lighting, and the tint/splat paths
 * take abs(normal.y). Two paths do NOT follow that compensation, and their
 * collision is the seam this fixes:
 *
 *  - Natural-preset diffuse lighting (flatShading:false) reads the raw SIGNED
 *    vertex normal; a downward normal gives NdotL < 0 → unlit.
 *  - reconcileBorderNormals (#107) canonicalises BORDER vertex normals to point
 *    up. On a back-facing tile those up-normals are then flipped down by the
 *    DoubleSide back-face path → the dark tile-edge bands (Natural only).
 *
 * This pass removes the root disagreement: on tile attach, if a tile's computed
 * normals face down, reverse its triangle winding and recompute, so the normals
 * face up AND the geometry is front-facing from above. Lighting is then correct
 * without the DoubleSide flip, and reconcile's up-canonicalisation is consistent
 * (a no-op) rather than harmful. It is self-correcting: a tile already wound
 * up-facing (e.g. a future pipeline regen, 03_build_terrain_tiles.py:199) is
 * left untouched, so this never re-introduces the seam by double-flipping.
 *
 * Only the triangle index order (winding) and the `normal` attribute are ever
 * written; `position` is byte-identical, so the collider and every LOS/ground
 * result are unaffected (hard constraints #1, #6). The material stays
 * DoubleSide (AC-N2). Pure and renderer-free, so it is unit-tested in Node,
 * following the terrainNormalReconcile/RouteRibbon geometry-builder pattern.
 * Never imports from raypath/.
 */
import type { BufferAttribute, BufferGeometry } from 'three';

/**
 * Reverses every triangle's winding in place (swap the 2nd and 3rd vertex of
 * each face), for indexed or non-indexed geometry. Flipping two of a triangle's
 * three vertices reverses its orientation, so a subsequent computeVertexNormals
 * yields normals pointing the opposite way.
 */
function reverseWinding(geometry: BufferGeometry): void {
  const index = geometry.getIndex();
  if (index) {
    const a = index.array as unknown as { [i: number]: number; length: number };
    for (let i = 0; i + 2 < a.length; i += 3) {
      const t = a[i + 1];
      a[i + 1] = a[i + 2];
      a[i + 2] = t;
    }
    index.needsUpdate = true;
    return;
  }
  // Non-indexed: swap vertices 1 and 2 of each triangle across every attribute,
  // so all per-vertex data (position and anything else) stays consistent.
  for (const name of Object.keys(geometry.attributes)) {
    const attr = geometry.getAttribute(name) as BufferAttribute;
    const arr = attr.array as unknown as { [i: number]: number };
    const it = attr.itemSize;
    for (let tri = 0; tri + 2 < attr.count; tri += 3) {
      for (let c = 0; c < it; c++) {
        const i1 = (tri + 1) * it + c;
        const i2 = (tri + 2) * it + c;
        const tmp = arr[i1];
        arr[i1] = arr[i2];
        arr[i2] = tmp;
      }
    }
    attr.needsUpdate = true;
  }
}

/**
 * Ensures `geometry`'s vertex normals face up (heightfield orientation),
 * reversing the triangle winding and recomputing when they face down. Returns
 * whether it changed the geometry (winding reversed), for diagnostics/tests.
 *
 * Detection uses the summed normal.y over all vertices: a terrain tile's true
 * surface faces up, so a negative sum means the whole tile is wound reversed.
 * The `>= 0` tie (a perfectly flat tile, or one whose up/down contributions
 * cancel) is treated as already-up: such a tile renders identically either way,
 * and treating it as up keeps the pass idempotent and free of thrash.
 */
export function ensureUpwardTerrainNormals(geometry: BufferGeometry): boolean {
  if (!geometry.getAttribute('normal')) geometry.computeVertexNormals();
  const normal = geometry.getAttribute('normal');
  let sumY = 0;
  for (let i = 0; i < normal.count; i++) sumY += normal.getY(i);
  if (sumY >= 0) return false;

  reverseWinding(geometry);
  geometry.deleteAttribute('normal');
  geometry.computeVertexNormals();
  return true;
}
